// Google Drive import for the Asset Library -- reuses the EXISTING server-side
// OAuth flow (server/routes/google-drive.js, mounted at /google-drive not
// /api) already built for card-ingest's Drive backup, rather than a new
// OAuth integration. That flow requests the `drive.file` scope (only files
// the app created OR the user explicitly picked), so browsing an arbitrary
// existing Drive folder needs Google's own Picker widget -- picking a file
// through it is what GRANTS this app drive.file access to it, not a plain
// `files.list` call (which would silently return nothing for anything the
// picker itself didn't already touch). Concretely: the Picker's own folder
// navigation lets a user drill into a folder and multi-select real files
// there; we never call `files.list` ourselves.
//
// Needs REACT_APP_GOOGLE_PICKER_API_KEY (a browser API key from the same
// Google Cloud project as GOOGLE_DRIVE_CLIENT_ID, with the "Google Picker
// API" enabled) -- a separate credential from the OAuth client id, required
// by Picker itself for quota/billing. Not yet present in this app's env; see
// .env.example.
import { api } from '../../services/apiClient';

const PICKER_API_KEY = process.env.REACT_APP_GOOGLE_PICKER_API_KEY || '';

export function isPickerConfigured() {
  return !!PICKER_API_KEY;
}

export async function getDriveStatus() {
  return api.get('/google-drive/status');
}

export async function getDriveAuthUrl() {
  const { url } = await api.get('/google-drive/auth-url');
  return url;
}

export async function getDriveAccessToken() {
  const { accessToken } = await api.get('/google-drive/access-token');
  return accessToken;
}

let pickerApiLoadPromise = null;

// Loads Google's own https://apis.google.com/js/api.js (the ONLY external
// script this needs -- Picker itself is a gapi module, not a separate CDN
// script), then the 'picker' module. Cached so repeated opens don't re-fetch.
function loadPickerApi() {
  if (pickerApiLoadPromise) return pickerApiLoadPromise;
  pickerApiLoadPromise = new Promise((resolve, reject) => {
    if (window.gapi?.picker) { resolve(); return; }
    const existing = document.getElementById('google-api-js');
    const onScriptReady = () => {
      window.gapi.load('picker', { callback: resolve, onerror: reject });
    };
    if (existing) { onScriptReady(); return; }
    const script = document.createElement('script');
    script.id = 'google-api-js';
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.onload = onScriptReady;
    script.onerror = () => reject(new Error('Failed to load Google API script'));
    document.body.appendChild(script);
  });
  return pickerApiLoadPromise;
}

// Opens the Picker (image-only DOCS_IMAGES view, folders navigable) and
// resolves with the array of picked {id, name, mimeType} docs, or null if
// the user cancelled.
export async function openDrivePicker({ accessToken }) {
  if (!isPickerConfigured()) {
    throw new Error('Google Picker is not configured for this app (REACT_APP_GOOGLE_PICKER_API_KEY missing)');
  }
  await loadPickerApi();
  return new Promise((resolve) => {
    const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS_IMAGES)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false); // browse INTO folders, but only files are pickable -- picking a folder itself would grant no readable access to its contents under the drive.file scope

    const picker = new window.google.picker.PickerBuilder()
      .setDeveloperKey(PICKER_API_KEY)
      .setOAuthToken(accessToken)
      .addView(view)
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .setCallback((data) => {
        if (data.action === window.google.picker.Action.PICKED) {
          resolve(data.docs || []);
        } else if (data.action === window.google.picker.Action.CANCEL) {
          resolve(null);
        }
      })
      .build();
    picker.setVisible(true);
  });
}

// Downloads a picked Drive doc's bytes and wraps them as a browser File --
// so the result can go straight into the EXISTING uploadAssets() pipeline
// (server/routes/assets.js's /upload route), no new backend endpoint needed.
export async function downloadDriveFileAsFile(doc, accessToken) {
  const response = await fetch(`https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) throw new Error(`Failed to download "${doc.name}" from Drive (${response.status})`);
  const blob = await response.blob();
  return new File([blob], doc.name, { type: doc.mimeType || blob.type });
}
